'use client';

import { useState, useEffect } from 'react';
import { GameSettingsType, AIDifficultyType, GameModeType, PlayerType } from '../types';

interface SettingsPanelProps {
  settings: GameSettingsType;
  onSettingsChange: (settings: Partial<GameSettingsType>) => void;
  isOpen: boolean;
  onClose: () => void;
}

export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  settings,
  onSettingsChange,
  isOpen,
  onClose,
}) => {
  const [localSettings, setLocalSettings] = useState<GameSettingsType>(settings);

  // Update local settings when props change
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('SettingsPanel - Submitting settings:', { ...localSettings });
    onSettingsChange({ ...localSettings });
    onClose();
  };

  // Handle game mode change
  const handleGameModeChange = (mode: GameModeType) => {
    setLocalSettings(prev => ({
      ...prev,
      gameMode: mode,
    }));
  };

  // Handle difficulty change
  const handleDifficultyChange = (difficulty: AIDifficultyType) => {
    setLocalSettings(prev => ({
      ...prev,
      aiDifficulty: difficulty,
    }));
  };

  // Handle player color change
  const handlePlayerColorChange = (color: PlayerType) => {
    setLocalSettings(prev => ({
      ...prev,
      humanPlayerColor: color,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
        <button
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          onClick={onClose}
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <h2 className="text-2xl font-bold mb-4 text-indigo-700">Game Settings</h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">Game Mode</label>
            <div className="flex gap-4">
              <button
                type="button"
                className={`flex-1 py-2 px-3 rounded-md border ${
                  localSettings.gameMode === 'human-vs-human'
                    ? 'bg-indigo-100 border-indigo-500 text-indigo-700'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => handleGameModeChange('human-vs-human')}
              >
                Human vs Human
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-3 rounded-md border ${
                  localSettings.gameMode === 'human-vs-ai'
                    ? 'bg-indigo-100 border-indigo-500 text-indigo-700'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => handleGameModeChange('human-vs-ai')}
              >
                Human vs AI
              </button>
            </div>
          </div>

          {localSettings.gameMode === 'human-vs-ai' && (
            <>
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">AI Difficulty</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    className={`py-2 rounded-md border ${
                      localSettings.aiDifficulty === 'easy'
                        ? 'bg-green-100 border-green-500 text-green-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handleDifficultyChange('easy')}
                  >
                    Easy
                  </button>
                  <button
                    type="button"
                    className={`py-2 rounded-md border ${
                      localSettings.aiDifficulty === 'medium'
                        ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handleDifficultyChange('medium')}
                  >
                    Medium
                  </button>
                  <button
                    type="button"
                    className={`py-2 rounded-md border ${
                      localSettings.aiDifficulty === 'hard'
                        ? 'bg-red-100 border-red-500 text-red-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handleDifficultyChange('hard')}
                  >
                    Hard
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">You Play As</label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    className={`flex-1 py-2 px-3 rounded-md border flex items-center justify-center gap-2 ${
                      localSettings.humanPlayerColor === 'BLACK'
                        ? 'bg-gray-800 text-white'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handlePlayerColorChange('BLACK')}
                  >
                    <div className="w-4 h-4 rounded-full bg-gray-900"></div>
                    Black (First)
                  </button>
                  <button
                    type="button"
                    className={`flex-1 py-2 px-3 rounded-md border flex items-center justify-center gap-2 ${
                      localSettings.humanPlayerColor === 'WHITE'
                        ? 'bg-gray-100 border-gray-400'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handlePlayerColorChange('WHITE')}
                  >
                    <div className="w-4 h-4 rounded-full bg-gray-100 border border-gray-400"></div>
                    White (Second)
                  </button>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 mt-8">
            <button
              type="button"
              className="py-2 px-4 border border-gray-300 rounded-md hover:bg-gray-50"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Save & Restart
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SettingsPanel;